import type { ResumeData } from "@reactive-resume/schema";
import {
  defaultLayoutForTemplate,
  preserveCustomSections,
  professionalLayouts,
  templateTypography,
} from "@reactive-resume/schema";
import type { Template } from "@reactive-resume/utils";

export type TemplateThemePreset = {
  background: string;
  text: string;
  primary: string;
};

// Couleurs affichées dans les miniatures des modèles.
const TEMPLATE_PRIMARY_COLORS: Record<Template, string> = {
  "citrus-bloom": "#f18f50",
  "plum-timeline": "#552745",
  "mono-content": "#272727",
  "linen-ledger": "#d7cbbb",
  "charcoal-monogram": "#c5b985",
  "cobalt-balance": "#004bac",
  "royal-line": "#004bac",
  "silver-ledger": "#d6e0e4",
  "navy-summit": "#304556",
  "dune-flow": "#cab9a8",
  "blue-horizon": "#456695",
  "slate-ledger": "#353d4d",
  "editorial-muse": "#bea68e",
  "sand-profile": "#916b57",
  "code-clarity": "#0c2152",
  "marketing-pulse": "#441733",
  "people-first": "#1c3a1f",
  "structural-line": "#06122b",
  "clinical-calm": "#104b4e",
  "sales-focus": "#073b88",
  "account-balance": "#0a4651",
  "legal-distinction": "#5c1420",
  "fiscal-arch": "#9b3d3d",
  "soft-contour": "#965c3f",
  "brand-mocha": "#a57e65",
  "forest-frame": "#2e422d",
  "azure-frame": "#243442",
  "sage-orbit": "#174d42",
  "project-flow": "#0a1c40",
  "fresh-start": "#6e4ca2",
  "blue-wave": "#1c4165",
};

export const getTemplateThemePreset = (template: Template): TemplateThemePreset => ({
  background: "#ffffff",
  text: "#000000",
  primary: TEMPLATE_PRIMARY_COLORS[template] ?? "#dc2626",
});

export const applyTemplateThemeToData = (data: ResumeData, template: Template): ResumeData => {
  const preset = getTemplateThemePreset(template);
  const typography = templateTypography[template];

  return {
    ...data,
    basics: {
      ...data.basics,
      picture: {
        ...data.basics.picture,
        size: professionalLayouts[template]
          ? template === "legal-distinction"
            ? 160
            : 150
          : data.basics.picture.size,
      },
    },
    metadata: {
      ...data.metadata,
      template,
      layout: preserveCustomSections(
        defaultLayoutForTemplate(template),
        Object.keys(data.sections.custom),
      ),
      typography: {
        ...data.metadata.typography,
        font: {
          family: typography?.family ?? "Arial",
          size: typography?.size ?? (professionalLayouts[template] ? 12 : 14),
          variants: typography?.variants ?? ["regular", "700"],
          subset: "latin",
        },
        ...(typography && { lineHeight: typography.lineHeight }),
      },
      theme: {
        ...data.metadata.theme,
        background: preset.background,
        text: preset.text,
        primary: preset.primary,
        palette: undefined,
      },
    },
  };
};
